import * as XLSX from "xlsx";

/**
 * Generates an Excel report from migration analysis data
 * @param {Object} analysisData Data collected during the analysis
 * @param {Object} questionnaireData Data collected in the questionnaire
 * @returns {Blob} Excel file as a Blob
 */
export const generateMigrationReport = (
  analysisData,
  questionnaireData = {}
) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Safely extract organization data
  const orgProfile = questionnaireData.orgProfile || {};
  const skuSelection = questionnaireData.skuSelection || [];
  const configSetup = questionnaireData.configSetup || {};
  const foundationalSetup = questionnaireData.foundationalSetup || {};

  // Add Summary sheet
  const summaryData = [
    ["Nexus Migration Analysis Report"],
    [""],
    ["Organization", orgProfile.name || "N/A"],
    ["Contact", orgProfile.contactName || "N/A"],
    ["Email", orgProfile.contactEmail || "N/A"],
    ["Generated On", new Date().toLocaleString()],
    [""],
    ["Selected SKUs", skuSelection.join(", ") || "None"],
    [""],
    ["Analysis Results"],
    ["Status", "Complete"],
    ["Issues Identified", analysisData?.issuesCount || 0],
    ["Issues Resolved", analysisData?.resolvedCount || 0],
    [
      "Estimated Completion Time",
      analysisData?.estimatedCompletionTime || "12 weeks",
    ],
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

  // Add Migration Mappings sheet
  const mappingsHeaders = [
    [
      "Type",
      "Legacy Name",
      "Legacy Code",
      "Description",
      "Workday Name",
      "Workday Code",
      "Workday Description",
      "Comments",
      "Action",
      "Status",
    ],
  ];

  // Array to collect all mapping data
  let mappingsData = [];

  // Helper function to process each configuration category
  const processConfigCategory = (category, categoryName) => {
    if (configSetup[category] && configSetup[category].length > 0) {
      return configSetup[category].map((item) => [
        categoryName,
        item.legacyName || "",
        item.legacyCode || "",
        item.description || "",
        item.newName || "",
        item.newCode || "",
        item.newDescription || "",
        item.comments || "",
        item.action || "Keep",
        item.action === "notRequired" ? "Not Migrated" : "Ready for Migration",
      ]);
    }
    return [];
  };

  // Process all configuration categories
  mappingsData = mappingsData.concat(
    processConfigCategory("employeeTypes", "Employee Type")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("cwTypes", "CW Type")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("payRateTypes", "Pay Rate Type")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("timeProfiles", "Time Profile")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("licenseIdTypes", "License ID Type")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("locationTypes", "Location Type")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("locationUsages", "Location Usage")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("terminationReasons", "Termination Reason")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("managementLevels", "Management Level")
  );
  mappingsData = mappingsData.concat(
    processConfigCategory("otherIds", "Other ID")
  );

  // If no mappings, add a placeholder row
  if (mappingsData.length === 0) {
    mappingsData.push([
      "No mapping data available",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
  }

  const mappingsSheet = XLSX.utils.aoa_to_sheet([
    ...mappingsHeaders,
    ...mappingsData,
  ]);
  XLSX.utils.book_append_sheet(workbook, mappingsSheet, "Config Mappings");

  // Add Foundational Data sheet
  const foundationalHeaders = [
    [
      "Type",
      "Legacy Name",
      "Legacy Code",
      "Description",
      "Workday Name",
      "Workday Code",
      "Workday Description",
      "Comments",
      "Action",
      "Status",
    ],
  ];

  // Process foundational setup categories
  let foundationalData = [];

  // Helper function to process each foundational category
  const processFoundationalCategory = (category, categoryName) => {
    if (foundationalSetup[category] && foundationalSetup[category].length > 0) {
      return foundationalSetup[category].map((item) => [
        categoryName,
        item.legacyName || "",
        item.legacyCode || "",
        item.description || "",
        item.newName || "",
        item.newCode || "",
        item.newDescription || "",
        item.comments || "",
        item.action || "Keep",
        item.action === "notRequired" ? "Not Migrated" : "Ready for Migration",
      ]);
    }
    return [];
  };

  // Process all foundational categories
  foundationalData = foundationalData.concat(
    processFoundationalCategory("regions", "Region/Country")
  );
  foundationalData = foundationalData.concat(
    processFoundationalCategory("companies", "Company")
  );
  foundationalData = foundationalData.concat(
    processFoundationalCategory("locations", "Location")
  );
  foundationalData = foundationalData.concat(
    processFoundationalCategory("costCenters", "Cost Center")
  );
  foundationalData = foundationalData.concat(
    processFoundationalCategory("departments", "Department")
  );
  foundationalData = foundationalData.concat(
    processFoundationalCategory("gradeProfiles", "Grade Profile")
  );
  foundationalData = foundationalData.concat(
    processFoundationalCategory("jobProfiles", "Job Profile")
  );

  // If no foundational data, add a placeholder row
  if (foundationalData.length === 0) {
    foundationalData.push([
      "No foundational data available",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
  }

  const foundationalSheet = XLSX.utils.aoa_to_sheet([
    ...foundationalHeaders,
    ...foundationalData,
  ]);
  XLSX.utils.book_append_sheet(
    workbook,
    foundationalSheet,
    "Foundational Data"
  );

  // Add Timeline sheet
  const timelineData = [
    [
      "Phase",
      "Start Date",
      "End Date",
      "Status",
      "Owner",
      "Dependencies",
      "Risks",
      "Notes",
    ],
    [
      "Discovery",
      "04/15/2025",
      "05/15/2025",
      "Complete",
      "Nexus AI",
      "None",
      "None",
      "Completed with AI assistance",
    ],
    [
      "Design",
      "05/16/2025",
      "06/30/2025",
      "Not Started",
      "Implementation Team",
      "Discovery",
      "Resource availability",
      "Workday expertise required",
    ],
    [
      "Configuration",
      "07/01/2025",
      "08/15/2025",
      "Not Started",
      "Implementation Team",
      "Design",
      "Configuration complexity",
      "Includes all configuration items",
    ],
    [
      "Testing",
      "08/16/2025",
      "09/30/2025",
      "Not Started",
      "QA Team",
      "Configuration",
      "Test case coverage",
      "Includes parallel testing",
    ],
    [
      "Deployment",
      "10/01/2025",
      "10/15/2025",
      "Not Started",
      "Operations Team",
      "Testing",
      "Data migration issues",
      "Cutover plan to be finalized",
    ],
    [
      "Go-Live",
      "10/16/2025",
      "10/16/2025",
      "Not Started",
      "All Teams",
      "Deployment",
      "System performance",
      "Support team on standby",
    ],
    [
      "Post Go-Live Support",
      "10/17/2025",
      "11/16/2025",
      "Not Started",
      "Support Team",
      "Go-Live",
      "User adoption",
      "Hypercare period",
    ],
  ];

  const timelineSheet = XLSX.utils.aoa_to_sheet(timelineData);
  XLSX.utils.book_append_sheet(workbook, timelineSheet, "Timeline");

  // Add Gap Analysis sheet
  const gapHeaders = [
    ["Category", "Issue", "Severity", "Impact", "Resolution", "Status"],
  ];

  const gapData = [
    [
      "Employee Benefits",
      "Missing mapping for custom benefit types",
      "High",
      "Could prevent correct benefit enrollment",
      "Benefit codes mapped and documented",
      "Resolved",
    ],
    [
      "Department Hierarchy",
      "Inconsistent hierarchy depth across departments",
      "Medium",
      "May impact reporting structure",
      "Standardized to 4-level hierarchy",
      "Resolved",
    ],
    [
      "Job Codes",
      "Several job codes without descriptions",
      "Low",
      "Minor reporting impact",
      "Added missing descriptions",
      "Resolved",
    ],
    [
      "Location Types",
      "Inconsistent usage of location types",
      "Medium",
      "May cause confusion in reporting",
      "Standardized location type definitions",
      "Resolved",
    ],
    [
      "Management Levels",
      "Overlap in management level definitions",
      "Medium",
      "Could cause hierarchy issues",
      "Redefined management levels with clear boundaries",
      "Resolved",
    ],
  ];

  const gapSheet = XLSX.utils.aoa_to_sheet([...gapHeaders, ...gapData]);
  XLSX.utils.book_append_sheet(workbook, gapSheet, "Gap Analysis");

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  return blob;
};

/**
 * Creates a download link for the generated report
 * @param {Blob} blob Excel file as a Blob
 * @returns {string} URL for downloading the file
 */
export const createDownloadLink = (blob) => {
  const url = window.URL.createObjectURL(blob);
  return url;
};
